import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './ArnoldPage.css';
import { API_ENDPOINTS } from '../../config/api';

function ArnoldPage() {
    const navigate = useNavigate();
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [authToken, setAuthToken] = useState(null);
    const messagesEndRef = useRef(null);
    
    // Estados para audio
    const [isRecording, setIsRecording] = useState(false);
    const [mediaRecorder, setMediaRecorder] = useState(null);
    const [audioChunks, setAudioChunks] = useState([]);
    const [isAudioMode, setIsAudioMode] = useState(false);
    const [hasAudio, setHasAudio] = useState(false);

    // Verificar si hay token en localStorage
    useEffect(() => {
        const token = localStorage.getItem('arnold_auth_token');
        if (token) {
            setAuthToken(token);
            
            // Mensaje de bienvenida
            const welcomeMessage = {
                id: Date.now(),
                type: 'arnold',
                text: "Hola. Soy Arnold, tu asistente personal. Estoy disponible para ayudarte con consultas y tareas. ¿En qué puedo asistirte?",
                timestamp: new Date()
            };
            setMessages([welcomeMessage]);
        } else {
            // Si no hay token, redirigir a la página principal
            navigate('/');
        }
    }, [navigate]);

    // Auto-scroll al final de los mensajes
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);


    // Función para iniciar grabación
    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const recorder = new MediaRecorder(stream);
            const chunks = [];

            recorder.ondataavailable = (event) => {
                chunks.push(event.data);
            };

            recorder.onstop = () => {
                setAudioChunks(chunks);
                setHasAudio(true);
            };

            recorder.start();
            setMediaRecorder(recorder);
            setIsRecording(true);
        } catch (error) {
            console.error('Error al acceder al micrófono:', error);
        }
    };

    // Función para detener grabación
    const stopRecording = () => {
        if (mediaRecorder && isRecording) {
            mediaRecorder.stop();
            setIsRecording(false);
            setMediaRecorder(null);
        }
    };

    // Función para enviar audio grabado
    const sendRecordedAudio = () => {
        if (audioChunks.length > 0) {
            const audioBlob = new Blob(audioChunks, { type: 'audio/wav' });
            sendAudioMessage(audioBlob);
            setHasAudio(false);
            setAudioChunks([]);
        }
    };

    // Función para cancelar audio
    const cancelAudio = () => {
        setHasAudio(false);
        setAudioChunks([]);
    };

    // Función para enviar audio
    const sendAudioMessage = async (audioBlob) => {
        const userMessage = {
            id: Date.now(),
            type: 'user',
            text: '[Audio]',
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        setIsLoading(true);

        try {
            const formData = new FormData();
            formData.append('audio', audioBlob, 'audio.wav');
            formData.append('timestamp', new Date().toISOString());

            const response = await axios.post(
                API_ENDPOINTS.ARNOLD_AUDIO(),
                formData,
                {
                    headers: {
                        'Authorization': `Basic ${btoa(`arnold:${authToken}`)}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            const arnoldMessage = {
                id: Date.now() + 1,
                type: 'arnold',
                text: response.data[0]?.output || "No pude procesar tu audio en este momento. Inténtalo de nuevo.",
                timestamp: new Date()
            };

            setMessages(prev => [...prev, arnoldMessage]);
        } catch (error) {
            console.error('Error al enviar audio a Arnold:', error);
            
            const errorMessage = {
                id: Date.now() + 1,
                type: 'arnold',
                text: "Estoy experimentando dificultades técnicas. Inténtalo de nuevo en unos momentos.",
                timestamp: new Date()
            };

            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!inputMessage.trim() || isLoading) return;

        const userMessage = {
            id: Date.now(),
            type: 'user',
            text: inputMessage,
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        setInputMessage('');
        setIsLoading(true);

        try {
            // Llamada al endpoint de Arnold
            const response = await axios.post(
                API_ENDPOINTS.ARNOLD_CHAT(),
                {
                    message: inputMessage,
                    timestamp: new Date().toISOString()
                },
                {
                    headers: {
                        'Authorization': `Basic ${btoa(`arnold:${authToken}`)}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            const arnoldMessage = {
                id: Date.now() + 1,
                type: 'arnold',
                text: response.data[0]?.output || "No pude procesar tu mensaje en este momento. Inténtalo de nuevo.",
                timestamp: new Date()
            };

            setMessages(prev => [...prev, arnoldMessage]);
        } catch (error) {
            console.error('Error al comunicarse con Arnold:', error);
            
            const errorMessage = {
                id: Date.now() + 1,
                type: 'arnold',
                text: "Estoy experimentando dificultades técnicas. Inténtalo de nuevo en unos momentos.",
                timestamp: new Date()
            };

            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };


    return (
        <div className="arnold-page">
            <div className="arnold-page-messages">
                {messages.map((message) => (
                    <div key={message.id} className={`message ${message.type}`}>
                        <div className="message-content">
                            <div className="message-text">{message.text}</div>
                            <div className="message-time">
                                {message.timestamp.toLocaleTimeString()}
                            </div>
                        </div>
                    </div>
                ))}
                {isLoading && (
                    <div className="message arnold">
                        <div className="message-content">
                            <div className="typing-indicator">
                                <span></span>
                                <span></span>
                                <span></span>
                            </div>
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            <div className="arnold-page-input">
                {/* Toggle entre modo texto y audio */}
                <div className="input-mode-toggle">
                    <button 
                        type="button"
                        className={`mode-button ${!isAudioMode ? 'active' : ''}`}
                        onClick={() => setIsAudioMode(false)}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                        </svg>
                        Texto
                    </button>
                    <button 
                        type="button"
                        className={`mode-button ${isAudioMode ? 'active' : ''}`}
                        onClick={() => setIsAudioMode(true)}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z"/>
                        </svg>
                        Audio
                    </button>
                </div>

                {/* Modo Texto */}
                {!isAudioMode && (
                    <form onSubmit={handleSendMessage} className="text-input-form">
                        <input
                            type="text"
                            value={inputMessage}
                            onChange={(e) => setInputMessage(e.target.value)}
                            placeholder="Escribe tu mensaje a Arnold..."
                            disabled={isLoading}
                            className="message-input"
                        />
                        <button 
                            type="submit" 
                            disabled={!inputMessage.trim() || isLoading}
                            className="send-button"
                        >
                            {isLoading ? '...' : '→'}
                        </button>
                    </form>
                )}

                {/* Modo Audio */}
                {isAudioMode && (
                    <div className="audio-input-form">
                        {!hasAudio ? (
                            <div className="audio-controls">
                                <button
                                    type="button"
                                    className={`record-button ${isRecording ? 'recording' : ''}`}
                                    onClick={isRecording ? stopRecording : startRecording}
                                    disabled={isLoading}
                                >
                                    {isRecording ? (
                                        <>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                                <rect x="6" y="6" width="12" height="12" rx="2"/>
                                            </svg>
                                            Detener
                                        </>
                                    ) : (
                                        <>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                                <circle cx="12" cy="12" r="10"/>
                                            </svg>
                                            Grabar
                                        </>
                                    )}
                                </button>
                            </div>
                        ) : (
                            <div className="audio-preview">
                                <div className="audio-info">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z"/>
                                    </svg>
                                    Audio grabado
                                </div>
                                <div className="audio-actions">
                                    <button
                                        type="button"
                                        className="action-button cancel"
                                        onClick={cancelAudio}
                                        disabled={isLoading}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                                        </svg>
                                        Cancelar
                                    </button>
                                    <button
                                        type="button"
                                        className="action-button send"
                                        onClick={sendRecordedAudio}
                                        disabled={isLoading}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                                        </svg>
                                        Enviar
                                    </button>
                                </div>
                            </div>
                        )}
                        {isLoading && (
                            <div className="audio-loading">
                                Procesando audio...
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default ArnoldPage;
